/**
 * `SendToPicker` — the CCM "smoothest UX" copy/move type-ahead fast-path
 * (handoff §1.2 + §1.3). A compact, command-style popover anchored to the
 * file row's "Send to…" action: type to filter the lawyer's cases (reusing
 * `useCaseSwitcher.resolveLocal` → `matchCasesLocally`), ↑/↓ + Enter to pick,
 * a Copy/Move toggle. Recent destinations (§1.3) ride at the top as one-tap
 * rows so the "back and forth" repeat is trivial.
 *
 * This is the FAST path; the H#4 `ResourceCopyMoveDialog` stays as the
 * discoverable fallback. Both funnel a pick through `useCopyMoveAction` so the
 * post-op behavior (recent + toast + move-undo) is identical.
 *
 * Low chrome by design: a small popover (not a full-screen modal), a
 * click-away backdrop, sub-second, keyboard-first. The destination is a SCOPE
 * (another case, or "Global") — the resource keeps its `sources/<name>` ref
 * (no rename / no folder choice in v1). The current scope is excluded
 * (`buildCopyMoveTargets`); org→org / same-case can't appear.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Command } from "cmdk";
import { useTranslation } from "react-i18next";
import { Clock, Copy, FilesIcon, Globe, Loader2, MoveRight } from "lucide-react";
import { useCases } from "@/hooks/useCases";
import { matchCasesLocally } from "@/hooks/useCaseSwitcher";
import {
  buildCopyMoveTargets,
  type CopyMoveTarget,
  type SourceScope,
} from "@/lib/copy-move-targets";
import { useRecentDestinationsStore, recentTargetsForSource } from "@/stores/useRecentDestinationsStore";
import { useCopyMoveAction } from "@/hooks/useCopyMoveAction";
import type { CopyMoveSource } from "@/components/workspace/ResourceCopyMoveDialog";
import { cn } from "@/lib/utils";

interface Props {
  /** The anchor element the popover positions against (the row action btn). */
  anchor: HTMLElement | null;
  /** Resource path, workspace-relative. Machine ref — used for the op,
   *  NEVER shown. */
  path: string;
  /** FRIENDLY display name (title → alias → tag). NEVER the OS filename
   *  (rule 6). */
  displayName: string;
  /** Optional @tag handle, shown as a small mono chip. */
  tag?: string;
  /** The source scope. */
  source: CopyMoveSource;
  onClose: () => void;
}

/** The source scope in the `SourceScope` shape (drives target build + the
 *  recent-chip validity filter). */
function sourceScopeOf(source: CopyMoveSource): SourceScope {
  return source.scope === "org"
    ? { scope: "org" }
    : { scope: "case", caseId: source.caseId };
}

export function SendToPicker({ anchor, path, displayName, tag, source, onClose }: Props) {
  const { t } = useTranslation();
  const { data: cases, isLoading: casesLoading } = useCases({ enabled: true });
  const recent = useRecentDestinationsStore((s) => s.recent);
  const { run, isPending } = useCopyMoveAction();
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState<"copy" | "move">("move");
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const srcScope = useMemo(() => sourceScopeOf(source), [source]);
  const orgLabel = t("resourceCopyMove.globalLabel");

  // All legal targets for this source (excludes current scope + org→org).
  const allTargets = useMemo(
    () => buildCopyMoveTargets(cases ?? [], srcScope, orgLabel),
    [cases, srcScope, orgLabel],
  );

  // Recent destinations valid for THIS source, intersected with the live
  // target set (a recent case may have been closed / become unaddressable).
  const recentTargets = useMemo(() => {
    const valid = recentTargetsForSource(recent, srcScope);
    const addressable = new Set(allTargets.map((tg) => tg.caseId));
    return valid.filter((d) => addressable.has(d.caseId)).slice(0, 3);
  }, [recent, srcScope, allTargets]);

  // Type-ahead filter over the case rows (reuse matchCasesLocally for the
  // accent/case-insensitive substring match). The org row is matched by a
  // simple label-contains so "global"/"geral" surfaces it. An empty query
  // shows everything.
  const filtered = useMemo<CopyMoveTarget[]>(() => {
    const q = query.trim();
    if (!q) return allTargets;
    const matchedCaseIds = new Set(
      matchCasesLocally(cases ?? [], q, 50).candidates.map((c) => c.caseId),
    );
    const ql = q.toLowerCase();
    return allTargets.filter((tg) =>
      tg.kind === "org"
        ? tg.title.toLowerCase().includes(ql) ||
          "global geral general".includes(ql)
        : matchedCaseIds.has(tg.caseId) ||
          tg.title.toLowerCase().includes(ql),
    );
  }, [allTargets, cases, query]);

  // The main (non-recent) list. When the recent chips are shown (no active
  // query), drop those rows from the main list so a recent destination
  // doesn't appear twice (which would also give cmdk two items with the same
  // value, breaking arrow-key navigation).
  const mainList = useMemo<CopyMoveTarget[]>(() => {
    if (query.trim()) return filtered;
    const recentIds = new Set(recentTargets.map((d) => d.caseId));
    return filtered.filter((tg) => !recentIds.has(tg.caseId));
  }, [filtered, recentTargets, query]);

  // Click-away + Escape close.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        anchor &&
        !anchor.contains(e.target as Node)
      ) {
        onClose();
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [anchor, onClose]);

  if (!anchor) return null;
  const rect = anchor.getBoundingClientRect();
  // Anchor below-left of the trigger, clamped into the viewport.
  const width = 288;
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
  const top = Math.min(rect.bottom + 4, window.innerHeight - 320);

  async function pick(target: CopyMoveTarget) {
    if (isPending) return;
    setError(null);
    const res = await run({ mode, path, name: displayName, target, source });
    if (res.ok) {
      onClose();
    } else {
      setError(res.error ?? t("resourceCopyMove.failed"));
    }
  }

  return createPortal(
    <div
      ref={panelRef}
      data-testid="send-to-picker"
      data-mode={mode}
      style={{ position: "fixed", left, top, width }}
      className="z-50 overflow-hidden rounded-xl border border-cream-200/60 bg-white shadow-[0_1px_2px_rgba(28,24,20,0.04),0_8px_24px_rgba(28,24,20,0.06)] dark:border-coffee-700/60 dark:bg-coffee-900"
    >
      {/* Copy/Move toggle — default Move; the toggle flips it. */}
      <div className="flex items-center gap-1 border-b border-cream-200/60 px-2 py-1.5 dark:border-coffee-700/60">
        <ModeToggle
          active={mode === "move"}
          onClick={() => setMode("move")}
          icon={MoveRight}
          label={t("resourceCopyMove.modeMove")}
          testId="send-to-mode-move"
        />
        <ModeToggle
          active={mode === "copy"}
          onClick={() => setMode("copy")}
          icon={Copy}
          label={t("resourceCopyMove.modeCopy")}
          testId="send-to-mode-copy"
        />
        {/* Identity — the FRIENDLY name (+ @tag chip); never the OS filename
            or path (rule 6). */}
        <span className="ml-auto flex min-w-0 items-center gap-1.5 pl-2">
          <span className="truncate text-caption text-cream-500">{displayName}</span>
          {tag && tag.trim() !== displayName && (
            <span className="shrink-0 truncate rounded bg-cream-100 px-1 py-0.5 font-mono text-[10px] text-coffee-500 dark:bg-coffee-800 dark:text-cream-400">
              {tag}
            </span>
          )}
        </span>
      </div>

      <Command shouldFilter={false} loop className="flex flex-col">
        <div className="border-b border-cream-200/60 px-2.5 py-2 dark:border-coffee-700/60">
          <Command.Input
            autoFocus
            value={query}
            onValueChange={setQuery}
            placeholder={t("resourceCopyMove.sendToPlaceholder")}
            data-testid="send-to-input"
            className="w-full bg-transparent text-body-sm text-coffee-900 placeholder:text-cream-500 focus:outline-none dark:text-cream-100 dark:placeholder:text-coffee-500"
          />
        </div>

        <Command.List className="max-h-64 overflow-y-auto p-1">
          {casesLoading && (
            <div className="flex items-center gap-2 px-2 py-3 text-caption text-cream-500">
              <Loader2 size={13} className="animate-spin" />
              {t("resourceCopyMove.loadingCases")}
            </div>
          )}

          {/* Recent destinations (§1.3) — only when there's no active query
              (a query means the lawyer is targeting a specific place). */}
          {!query.trim() && recentTargets.length > 0 && (
            <Command.Group
              heading={t("resourceCopyMove.recentHeading")}
              className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-caption [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-cream-500"
            >
              {recentTargets.map((tg) => (
                <PickerRow
                  key={`recent:${tg.caseId}`}
                  target={tg}
                  recent
                  onSelect={() => pick(tg)}
                />
              ))}
            </Command.Group>
          )}

          {/* Empty state — only when there's genuinely nothing RENDERED
              (no main row, and the recent chips aren't being shown — they're
              suppressed while a query is active). */}
          {!casesLoading &&
            mainList.length === 0 &&
            (query.trim() !== "" || recentTargets.length === 0) && (
              <div
                data-testid="send-to-empty"
                className="px-3 py-4 text-center text-caption text-cream-500"
              >
                {query.trim()
                  ? t("resourceCopyMove.sendToNoMatch", { query: query.trim() })
                  : t("resourceCopyMove.noTargets")}
              </div>
            )}

          {mainList.map((tg) => (
            <PickerRow
              key={`${tg.kind}:${tg.caseId}`}
              target={tg}
              onSelect={() => pick(tg)}
            />
          ))}
        </Command.List>
      </Command>

      {isPending && (
        <div className="flex items-center gap-2 border-t border-cream-200/60 px-3 py-1.5 text-caption text-cream-500 dark:border-coffee-700/60">
          <Loader2 size={12} className="animate-spin" />
          {mode === "copy"
            ? t("resourceCopyMove.confirmCopy")
            : t("resourceCopyMove.confirmMove")}
          …
        </div>
      )}

      {error && (
        <div className="border-t border-cream-200/60 bg-red-50 px-3 py-1.5 text-caption text-red-700 dark:border-coffee-700/60 dark:bg-red-950 dark:text-red-400">
          {error}
        </div>
      )}
    </div>,
    document.body,
  );
}

function ModeToggle({
  active,
  onClick,
  icon: Icon,
  label,
  testId,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Copy;
  label: string;
  testId: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      data-active={active ? "true" : undefined}
      className={cn(
        "flex items-center gap-1 rounded-md px-2 py-1 text-caption font-medium transition-colors",
        active
          ? "bg-gold-500/15 text-gold-700 dark:text-gold-400"
          : "text-cream-500 hover:bg-cream-100/60 dark:hover:bg-coffee-800/60",
      )}
    >
      <Icon size={12} />
      {label}
    </button>
  );
}

function PickerRow({
  target,
  recent,
  onSelect,
}: {
  target: CopyMoveTarget;
  recent?: boolean;
  onSelect: () => void;
}) {
  return (
    <Command.Item
      value={`${target.kind}-${target.caseId} ${target.title}`}
      onSelect={onSelect}
      data-testid="send-to-target"
      data-target-kind={target.kind}
      data-target-id={target.caseId}
      className={cn(
        "flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-body-sm text-coffee-700 outline-none transition-colors",
        "data-[selected=true]:bg-cream-100/60 dark:text-cream-200 dark:data-[selected=true]:bg-coffee-800/60",
      )}
    >
      {recent ? (
        <Clock size={13} className="shrink-0 text-cream-500" />
      ) : target.kind === "org" ? (
        <Globe size={13} className="shrink-0 text-cream-500" />
      ) : (
        <FilesIcon size={13} className="shrink-0 text-cream-500" />
      )}
      <span className="flex-1 truncate">{target.title}</span>
      {target.kind === "case" && target.status && (
        <span className="shrink-0 text-caption uppercase tracking-wide text-cream-500">
          {target.status}
        </span>
      )}
    </Command.Item>
  );
}
